<div class="ks-container ks-sessions">
  <header class="ks-page-header ks-rise-1">
    <div>
      <h1 class="ks-page-title">Sesiones</h1>
      <p class="ks-page-subtitle">
        Sesiones abiertas y cerradas de la app móvil. Cerrar una sesión desconecta el dispositivo de
        inmediato.
      </p>
    </div>

    @if (loaded() && !loading() && !error()) {
      <p class="ks-sessions__count ks-nums" aria-live="polite">
        <strong>{{ total() }}</strong>
        {{ total() === 1 ? 'sesión' : 'sesiones' }}
      </p>
    }
  </header>

  <div class="ks-filter-bar ks-rise-2">
    <div class="ks-filter-bar__controls">
      <!--
        ng-select, no <select>: todos los desplegables de la consola deben
        permitir BUSCAR. El id del input interno lo pone `labelForId`, asi que el
        `<label for>` de arriba sigue apuntando al elemento que recibe el foco.
        "Todas las sesiones" queda como item REAL de la lista (no como
        placeholder) para no cambiar el comportamiento del select nativo que
        reemplaza.
      -->
      <div class="ks-sessions__filter">
        <label class="visually-hidden" for="ks-sessions-filter">Filtrar sesiones por estado</label>
        <ng-select
          labelForId="ks-sessions-filter"
          [items]="activeOptions"
          bindLabel="label"
          bindValue="value"
          [ngModel]="activeFilter()"
          (change)="onActiveFilter($event)"
          placeholder="Todas las sesiones"
          notFoundText="Sin resultados"
          clearAllText="Quitar selección"
          loadingText="Cargando…"
          typeToSearchText="Escribe para buscar"
          [searchable]="true"
          [clearable]="true" />
      </div>
    </div>

    <div class="ks-filter-bar__actions">
      <button
        type="button"
        class="ks-btn ks-btn--ghost ks-btn--sm"
        [disabled]="!hasFilters()"
        (click)="clearFilters()"
      >
        <i class="bi bi-x-circle" aria-hidden="true"></i>
        Limpiar filtros
      </button>
    </div>
  </div>

  <section class="ks-panel ks-rise-3" aria-labelledby="ks-sessions-heading">
    <h2 class="visually-hidden" id="ks-sessions-heading">Listado de sesiones</h2>

    <!-- Orden obligatorio: carga -> error -> vacio -> tabla. El estado vacio
         solo puede aparecer cuando ya se sabe con certeza que no hay datos. -->
    @if (!loaded() || loading()) {
      <div class="ks-sessions__skeleton">
        <app-skeleton [count]="8" height="44px" label="Cargando sesiones" />
      </div>
    } @else if (error()) {
      <app-empty-state
        icon="wifi-off"
        title="No se pudieron cargar las sesiones"
        [message]="error()!"
      >
        <button type="button" class="ks-btn ks-btn--glass" (click)="load()">
          <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
          Reintentar
        </button>
      </app-empty-state>
    } @else if (sessions().length === 0) {
      @if (hasFilters()) {
        <app-empty-state
          icon="funnel"
          title="Sin sesiones con ese filtro"
          message="No hay sesiones que coincidan con el estado seleccionado."
        >
          <button type="button" class="ks-btn ks-btn--glass" (click)="clearFilters()">
            <i class="bi bi-x-circle" aria-hidden="true"></i>
            Limpiar filtros
          </button>
        </app-empty-state>
      } @else {
        <app-empty-state
          icon="laptop"
          title="Todavía no hay sesiones"
          message="Aquí aparecerán las sesiones en cuanto alguien inicie sesión desde la app móvil."
        />
      }
    } @else {
      <div class="ks-table-wrap">
        <table class="ks-table ks-table--compact ks-table--sticky-first">
          <caption class="visually-hidden">
            Sesiones de la app móvil. Cada fila se puede desplegar para ver la ficha completa.
          </caption>

          <thead>
            <tr>
              <th scope="col">Dispositivo</th>
              <th scope="col">Usuario</th>
              <th scope="col">App</th>
              <th scope="col">Sistema operativo</th>
              <th scope="col">Ubicación</th>
              <th scope="col">Inicio</th>
              <th scope="col">Estado</th>
              <th scope="col" class="ks-sessions__th-actions">Acciones</th>
            </tr>
          </thead>

          <tbody>
            @for (session of sessions(); track session.sessionId) {
              <tr [class.ks-sessions__row--open]="expandedId() === session.sessionId">
                <td>
                  <span class="ks-sessions__strong">{{ session.deviceName || session.model }}</span>
                  <span class="ks-sessions__sub">
                    {{ session.manufacturer }} {{ session.model }}
                  </span>
                </td>

                <td>
                  <a class="ks-sessions__user" [routerLink]="['/dashboard/users', session.uidUser]">
                    <i class="bi bi-person" aria-hidden="true"></i>
                    <code>{{ session.uidUser }}</code>
                  </a>
                </td>

                <td>
                  <span class="ks-nums">{{ session.appVersion }}</span>
                  <span class="ks-sessions__sub">Build {{ session.appBuild }}</span>
                </td>

                <td>
                  <span>{{ session.osVersion }}</span>
                  <span class="ks-sessions__sub">{{ session.osRelease }}</span>
                </td>

                <td>
                  <span>{{ session.location || '—' }}</span>
                  <span class="ks-sessions__sub ks-nums">{{ session.ip }}</span>
                </td>

                <td>{{ session.createdAt | dateToMoment }}</td>

                <td>
                  <span
                    class="ks-badge"
                    [class]="session.active ? 'ks-badge--success' : 'ks-badge--muted'"
                  >
                    {{ session.active ? 'Activa' : 'Cerrada' }}
                  </span>
                </td>

                <td>
                  <div class="ks-table__actions">
                    <button
                      type="button"
                      class="ks-icon-btn"
                      [attr.aria-expanded]="expandedId() === session.sessionId"
                      [attr.aria-controls]="'ks-session-detail-' + session.sessionId"
                      [attr.aria-label]="
                        (expandedId() === session.sessionId ? 'Ocultar' : 'Ver') +
                        ' el detalle de la sesión de ' +
                        (session.deviceName || session.model)
                      "
                      (click)="toggle(session)"
                    >
                      <i
                        class="bi"
                        [class.bi-chevron-down]="expandedId() !== session.sessionId"
                        [class.bi-chevron-up]="expandedId() === session.sessionId"
                        aria-hidden="true"
                      ></i>
                    </button>

                    <button
                      type="button"
                      class="ks-icon-btn ks-icon-btn--danger"
                      [disabled]="!session.active"
                      [attr.aria-label]="
                        'Cerrar la sesión de ' + (session.deviceName || session.model)
                      "
                      (click)="closeSession(session)"
                    >
                      <i class="bi bi-power" aria-hidden="true"></i>
                    </button>
                  </div>
                </td>
              </tr>

              @if (expandedId() === session.sessionId) {
                <tr class="ks-sessions__detail-row">
                  <td colspan="8" [attr.id]="'ks-session-detail-' + session.sessionId">
                    <div class="ks-inset ks-sessions__detail">
                      <dl class="ks-sessions__detail-grid">
                        <div>
                          <dt>Identificador de sesión</dt>
                          <dd>
                            <code>{{ session.sessionId }}</code>
                          </dd>
                        </div>

                        <div>
                          <dt>Identificador de dispositivo</dt>
                          <dd>
                            <code>{{ session.deviceId }}</code>
                          </dd>
                        </div>

                        <div>
                          <dt>Descripción</dt>
                          <dd>{{ session.deviceDescription || '—' }}</dd>
                        </div>

                        <div>
                          <dt>Marca y modelo</dt>
                          <dd>{{ session.brand }} · {{ session.model }}</dd>
                        </div>

                        <div>
                          <dt>Fabricante</dt>
                          <dd>{{ session.manufacturer || '—' }}</dd>
                        </div>

                        <div>
                          <dt>Sistema operativo</dt>
                          <dd>{{ session.osVersion }} ({{ session.osRelease }})</dd>
                        </div>

                        <div>
                          <dt>Aplicación</dt>
                          <dd>{{ session.appVersion }} · build {{ session.appBuild }}</dd>
                        </div>

                        <div>
                          <dt>Dirección IP</dt>
                          <dd class="ks-nums">{{ session.ip || '—' }}</dd>
                        </div>

                        <div>
                          <dt>Ubicación</dt>
                          <dd>{{ session.location || '—' }}</dd>
                        </div>

                        <div>
                          <dt>Coordenadas</dt>
                          <dd>
                            @if (hasCoordinates(session)) {
                              <a
                                class="ks-sessions__map"
                                [href]="mapsUrl(session)"
                                target="_blank"
                                rel="noopener"
                              >
                                <i class="bi bi-geo-alt" aria-hidden="true"></i>
                                {{ session.latitude }}, {{ session.longitude }}
                                <span class="visually-hidden">(se abre en una pestaña nueva)</span>
                              </a>
                            } @else {
                              <span class="ks-muted">Sin coordenadas</span>
                            }
                          </dd>
                        </div>

                        <div>
                          <dt>Inicio de sesión</dt>
                          <dd>{{ session.createdAt | dateToMoment }}</dd>
                        </div>

                        <div>
                          <dt>Cierre de sesión</dt>
                          <dd>
                            @if (session.closedAt) {
                              {{ session.closedAt | dateToMoment }}
                            } @else {
                              <span class="ks-muted">Sigue activa</span>
                            }
                          </dd>
                        </div>

                        <div>
                          <dt>Cerrada por</dt>
                          <dd>
                            @if (session.uidClosedBy) {
                              <code>{{ session.uidClosedBy }}</code>
                            } @else {
                              <span class="ks-muted">—</span>
                            }
                          </dd>
                        </div>

                        <div>
                          <dt>Última actualización</dt>
                          <dd>{{ session.updatedAt | dateToMoment }}</dd>
                        </div>
                      </dl>
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <app-pagination
        [total]="total()"
        [page]="page()"
        [pageSize]="pageSize()"
        (pageChange)="onPageChange($event)"
      />
    }
  </section>
</div>
